import * as MUI from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { SnackbarContext } from "../../../contexts/Snackbar";
import useConfirm from "../../../hooks/useConfirm";
import { useGetClinics } from "../../../hooks/useGetClinics";
import { useGetTiers } from "../../../hooks/useGetTiers";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import ClinicBulkActions from "./components/ClinicBulkActions";
import ClinicDetailsDialog from "./components/ClinicDetailsDialog";
import ClinicListTable from "./components/ClinicListTable";
import ClinicListToolbar from "./components/ClinicListToolbar";
import MissingDataAlert from "./components/MissingDataAlert";
import useClinicListMutations from "./hooks/useClinicListMutations";
import useClinicListParams from "./hooks/useClinicListParams";
import useVisibleClinics from "./hooks/useVisibleClinics";
import { CLINIC_TYPE_LABELS } from "./utils/clinicFields";
import { clinicMatchKey } from "./utils/parseClinicSpreadsheet";

function toImportPayload({ id, ...clinic }) {
  return clinic;
}

export default function ClinicListPage() {
  const { t } = useTranslation();
  const { handleSnackbar } = useContext(SnackbarContext);
  const { data: clinics, isLoading } = useGetClinics();
  const { data: tiers } = useGetTiers();
  const {
    importClinics,
    activateClinics,
    deactivateClinics,
    changeTier,
    changeType,
    toggleActivation,
    updateClinic,
  } = useClinicListMutations();
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    null,
    t("Yes"),
    t("No"),
  );
  const { search, filters, sort } = useClinicListParams();
  const [pendingRows, setPendingRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [detailsClinicId, setDetailsClinicId] = useState(null);

  const savedClinics = useMemo(() => clinics ?? [], [clinics]);
  const rows = useVisibleClinics({
    pendingRows,
    savedClinics,
    tiers,
    search,
    filters,
    sort,
  });
  const selectedPending = pendingRows.filter((row) => selectedIds.has(row.id));
  const selectedActive = savedClinics.filter(
    (clinic) => clinic.active && selectedIds.has(clinic.id),
  );
  const selectedInactive = savedClinics.filter(
    (clinic) => !clinic.active && selectedIds.has(clinic.id),
  );
  const selectedSaved = savedClinics.filter((clinic) => selectedIds.has(clinic.id));
  const detailsClinic = useMemo(() => {
    const pendingRow = pendingRows.find((row) => row.id === detailsClinicId);
    if (pendingRow) return { ...pendingRow, isPending: true };
    return savedClinics.find((clinic) => clinic.id === detailsClinicId);
  }, [pendingRows, savedClinics, detailsClinicId]);

  function notifySkipped(count) {
    if (count === 0) return;
    handleSnackbar(
      t("ClinicImportSkippedExisting", { count }),
      snackbarSeverity.WARNING,
      "filled",
      true,
      false,
    );
  }

  async function askConfirmation(message) {
    getMessage(message);
    return confirm();
  }

  function deselect(ids) {
    setSelectedIds((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleParsed(parsedRows) {
    const knownKeys = new Set(
      savedClinics.map((clinic) => clinicMatchKey(clinic.title, clinic.city)),
    );
    const newRows = parsedRows.filter((row) => {
      const key = clinicMatchKey(row.title, row.city);
      if (knownKeys.has(key)) return false;
      knownKeys.add(key);
      return true;
    });
    const skipped = parsedRows.length - newRows.length;
    notifySkipped(skipped);
    setPendingRows(newRows);
    setSelectedIds(new Set(newRows.map((row) => row.id)));
  }

  async function handleImport() {
    const confirmed = await askConfirmation(
      t("ImportClinicsConfirm", { count: selectedPending.length }),
    );
    if (!confirmed) return;
    const submittedIds = selectedPending.map((row) => row.id);
    importClinics.mutate(selectedPending.map(toImportPayload), {
      onSuccess: ({ skippedRows = [] }) => {
        setPendingRows((prev) => prev.filter((row) => !submittedIds.includes(row.id)));
        deselect(submittedIds);
        notifySkipped(skippedRows.length);
      },
    });
  }

  async function handleBulkActivate() {
    const confirmed = await askConfirmation(
      t("ActivateClinicsConfirm", { count: selectedInactive.length }),
    );
    if (!confirmed) return;
    const ids = selectedInactive.map((clinic) => clinic.id);
    activateClinics.mutate(ids, { onSuccess: () => deselect(ids) });
  }

  async function handleBulkDeactivate() {
    const confirmed = await askConfirmation(
      t("DeactivateClinicsConfirm", { count: selectedActive.length }),
    );
    if (!confirmed) return;
    const ids = selectedActive.map((clinic) => clinic.id);
    deactivateClinics.mutate(ids, { onSuccess: () => deselect(ids) });
  }

  async function handleToggleActivation(clinic) {
    const confirmed = await askConfirmation(
      t(clinic.active ? "ClinicDeactivateMessage" : "ClinicActivateMessage"),
    );
    if (confirmed) toggleActivation.mutate(clinic);
  }

  async function handleTierChange(clinic, tier) {
    const confirmed = await askConfirmation(
      t("ChangeClinicTierConfirm", { clinic: clinic.title, tier: t(tier.title) }),
    );
    if (confirmed) changeTier.mutate({ tierId: tier.id, clinicIds: [clinic.id] });
  }

  async function handleTypeChange(clinic, clinicType) {
    const confirmed = await askConfirmation(
      t("ChangeClinicTypeConfirm", {
        clinic: clinic.title,
        type: t(CLINIC_TYPE_LABELS[clinicType]),
      }),
    );
    if (confirmed) changeType.mutate({ clinicType, clinicIds: [clinic.id] });
  }

  async function handleBulkTierChange(tier) {
    const confirmed = await askConfirmation(
      t("ChangeClinicsTierConfirm", { count: selectedSaved.length, tier: t(tier.title) }),
    );
    if (!confirmed) return;
    const ids = selectedSaved.map((clinic) => clinic.id);
    changeTier.mutate({ tierId: tier.id, clinicIds: ids }, { onSuccess: () => deselect(ids) });
  }

  async function handleSaveDetails({ isPending, ...clinic }) {
    if (isPending) {
      setPendingRows((prev) =>
        prev.map((row) => (row.id === clinic.id ? clinic : row)),
      );
      setDetailsClinicId(null);
      return true;
    }
    try {
      await updateClinic.mutateAsync(clinic);
      return true;
    } catch {
      return false;
    }
  }

  return (
    <PageContentWrapper maxWidth="xl">
      <MUI.Stack spacing={2}>
        <MissingDataAlert pendingRows={pendingRows} onEditClinic={setDetailsClinicId} />
        <ClinicListToolbar onParsed={handleParsed} />

        <ClinicBulkActions
          showImport={pendingRows.length > 0}
          counts={{
            import: selectedPending.length,
            activate: selectedInactive.length,
            deactivate: selectedActive.length,
            changeTier: selectedSaved.length,
          }}
          busy={{
            import: importClinics.isPending,
            activate: activateClinics.isPending,
            deactivate: deactivateClinics.isPending,
            changeTier: changeTier.isPending,
          }}
          onSelectAll={() => setSelectedIds(new Set(rows.map((row) => row.id)))}
          onDeselectAll={() => setSelectedIds(new Set())}
          onImport={handleImport}
          onActivate={handleBulkActivate}
          onDeactivate={handleBulkDeactivate}
          onChangeTier={handleBulkTierChange}
        />

        <ClinicListTable
          clinics={rows}
          isLoading={isLoading}
          selectedIds={selectedIds}
          rowActions={{
            toggleSelected,
            openDetails: setDetailsClinicId,
            changeTier: handleTierChange,
            changeType: handleTypeChange,
            toggleActivation: handleToggleActivation,
          }}
        />
      </MUI.Stack>

      <ClinicDetailsDialog
        clinic={detailsClinic}
        onClose={() => setDetailsClinicId(null)}
        onSave={handleSaveDetails}
      />
      <ConfirmationDialog />
    </PageContentWrapper>
  );
}
